/**
 * Touch density for the control cell of a settings row.
 *
 * Extracted from `settingsLayout.tsx` rather than inlined: that file is over
 * its 300-line cap, and the rule below needs more explanation than it has
 * characters — which is the shape of thing that belongs behind a name.
 *
 * @module
 */

/**
 * The control cell's layout and touch density.
 *
 * This replaced `max-sm:[&_[data-slot=button]]:min-h-11`, which was wrong in
 * two independent ways and neither was visible in a screenshot:
 *
 *  - `min-h-11` is 44px at the default root and shrinks with the user's
 *    interface-size setting, so it never reached the 48px platform minimum.
 *    `DESIGN.md` -> "The two-rem trap" is explicit that the numeric scale
 *    cannot express this number; `min-h-touch` is a literal 48px and can.
 *  - `max-sm:` ends at 640px, while Forge's touch/pointer boundary is `md:` at
 *    768px. Controls in the 640-767 band fell back to desktop density — a
 *    measured 32px control and an 18px switch on a viewport still driven by a
 *    finger, i.e. worse than the phone case that got all the attention.
 *
 * `sm:` still does the layout reflow — the control leaves the stack for the
 * right-hand column. Only the DENSITY moved to the `md` boundary, which is the
 * split the standard asks for.
 *
 * Bounded with `max-md:` rather than written as a base rule plus an `md:`
 * reset, and that is deliberate. The `Select` and `Button` primitives carry
 * their own desktop-first scale (`min-h-9 sm:min-h-8`); undoing a base rule
 * needs an `md:` reset, and `min-h-0` there does not restore the primitive's
 * value — it erases it. That measured as desktop selects dropping 32px -> 22px,
 * invisible to every narrow-viewport check. Bounding the override below `md`
 * leaves every pointer width byte-identical. Turning those primitives around is
 * the correct deeper fix and a far wider change than this rule.
 *
 * `settingsRowMobile.browser.tsx` measures both halves: 320/390/700 for the
 * targets, 1024 for the parity that catches the erasure above.
 */
export const SETTINGS_ROW_CONTROL_CLASS =
  "flex w-full shrink-0 items-center gap-2 max-md:[&_[data-slot=button]]:min-h-touch max-md:[&_[data-slot=button]]:rounded-xl max-md:[&_[data-slot=select-trigger]]:min-h-touch max-md:[&_[data-slot=select-trigger]]:rounded-xl sm:w-auto sm:justify-end";

/**
 * The settings page's vertical rhythm.
 *
 * `gap-12` unprefixed made 48px the PHONE's section gap as well as the
 * desktop one, which is the dead space a seven-row page reads as. Touch gets
 * 32px and `md:` restores 48px exactly.
 *
 * The bottom padding folds in the home-indicator inset instead of stacking on
 * top of it: the app shell owns the TOP inset and uses `svh`, but a scroller
 * ending in a flat `pb-7` put its last row under the indicator on a notched
 * phone with `viewport-fit=cover`.
 */
export const SETTINGS_PAGE_SCROLL_CLASS =
  "flex-1 overflow-y-auto px-4 pt-6 pb-[calc(--spacing(7)+env(safe-area-inset-bottom,0px))] sm:px-8 md:pt-12 md:pb-[calc(--spacing(10)+env(safe-area-inset-bottom,0px))]";

/** Section rhythm: touch spacing by default, the original desktop gap from `md`. */
export const SETTINGS_PAGE_STACK_CLASS = "mx-auto flex w-full max-w-4xl flex-col gap-8 md:gap-12";
